import { isToday } from 'date-fns';
import React from 'react';
import { Pressable } from 'react-native';

import { Box, Center, Text, VStack, theme } from '@squire/ui';

import { DayStripesOverlay } from '@/components/_shared/DayStripesOverlay';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { pressedState } from '@/utils/a11y';
import { DateFormat, formatDate } from '@/utils/dateUtils';

import { styles } from './styles';
import type { CalendarDayProps } from './types';

const ChooseTimeCalendarDay = (props: CalendarDayProps) => {
  const {
    date,
    isSelected,
    isDisabled,
    isWaitlistOnly = false,
    availabilityCount,
    onPress,
    testID,
  } = props;

  const { t, i18n } = useScreenTranslations('ChooseTimeCalendarDay');
  const isTodayDate = isToday(date);

  const fill: 'available' | 'waitlistOnly' | 'disabled' = isDisabled
    ? 'disabled'
    : isWaitlistOnly
      ? 'waitlistOnly'
      : 'available';

  styles.useVariants({ fill, selected: isSelected });

  const dayNumber = formatDate(date, DateFormat.DAY_NUMBER, i18n.language);
  const dayName = formatDate(date, DateFormat.WEEKDAY_UPPERCASE, i18n.language);

  const getAccessibilityLabel = () => {
    const dateStr = formatDate(
      date,
      DateFormat.USER_FRIENDLY_FULL_DATE,
      i18n.language,
    );
    if (isDisabled) {
      return `${dateStr}, ${t('accessibility.notAvailable')}`;
    }
    if (availabilityCount === 0) {
      return `${dateStr}, ${t('accessibility.noTimesAvailable')}`;
    }
    const timesText =
      availabilityCount === 1
        ? t('accessibility.oneTime')
        : t('accessibility.multipleTimes', { count: availabilityCount });
    return `${dateStr}, ${timesText} ${t('accessibility.available')}`;
  };

  return (
    <Pressable
      style={styles.container}
      onPress={isDisabled ? undefined : onPress}
      disabled={isDisabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={getAccessibilityLabel()}
      {...pressedState(isSelected)}
      aria-disabled={isDisabled || undefined}
      accessibilityHint={
        !isDisabled ? t('accessibility.tapToSelect') : undefined
      }
    >
      <VStack centerVertical space={theme.spacing[3]}>
        <Center style={styles.indicatorRing}>
          <Center style={styles.dayCircle}>
            {fill === 'disabled' && (
              <DayStripesOverlay
                testID={testID ? `${testID}-stripes` : undefined}
              />
            )}
            <Text variant="lStrong" style={styles.dayNumber}>
              {dayNumber}
            </Text>
            {isTodayDate && (
              <Box
                testID={testID ? `${testID}-today-indicator` : undefined}
                style={styles.todayIndicator}
              />
            )}
          </Center>
        </Center>

        <Text
          variant={isSelected ? 'sStrong' : 's'}
          color={isSelected ? 'textPrimary' : 'textTertiary'}
        >
          {dayName}
        </Text>
      </VStack>
    </Pressable>
  );
};

ChooseTimeCalendarDay.displayName = 'ChooseTimeCalendarDay';
export { ChooseTimeCalendarDay };
